import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { PageI } from '@interfaces/page.interface';
import { TabulationFilters, TabulationI, TabulationLevelE } from '@interfaces/tabulation.interface';
import { BehaviorSubject, combineLatest, filter, firstValueFrom, map, Observable } from 'rxjs';
import { UserHelperService } from '@helpers/user-helper.service';
import { UserAttributesI } from '@interfaces/user.interface';
import { TabulationsSocketService } from '@events/tabulations/tabulations-socket.service';
import { ChangeOperationCollectionEventI } from '@interfaces/change-operation-collection-event.interface';
import { ChannelI } from '@interfaces/channel.interface';

@Injectable({
  providedIn: 'root',
})
export class TabulationService {
  private baseUrl = environment.OMNISMART_API_CHAT_PERSISTENCE;
  private slug_url = 'tabulation';
  private tabulationsSubject = new BehaviorSubject<TabulationI[]>([]);
  private _tabulations$ = this.tabulationsSubject.asObservable();
  private tabulationsLoadedSubject = new BehaviorSubject<boolean>(false);
  public tabulationsLoaded$ = this.tabulationsLoadedSubject.asObservable();
  constructor(
    private http: HttpClient,
    private userHelper: UserHelperService,
    private tabulationsSocketService: TabulationsSocketService
  ) {
    this.tabulationsSocketService.change$.subscribe((change) => {
      if (!change) {
        return;
      }
      this.treatCrudOperations(change);
    });
  }

  public get tabulations$(): Observable<TabulationI[]> {
    return combineLatest([this.tabulationsLoaded$, this._tabulations$]).pipe(
      filter(([loaded, _]) => !!loaded),
      map(([_, tabulations]) => tabulations)
    );
  }

  private async treatCrudOperations(change: ChangeOperationCollectionEventI<TabulationI>) {
    let tabulations = await firstValueFrom(this.tabulations$);
    const { operation, data } = change;
    if (operation === 'delete') {
      this.deleteRecursive(tabulations, data);
      this.tabulationsSubject.next(tabulations);
    } else if (operation === 'update') {
      this.updatePartialRecursive(tabulations, data);
      this.tabulationsSubject.next(tabulations);
    } else if (operation === 'create') {
      tabulations.push(data as TabulationI);
      this.tabulationsSubject.next(tabulations);
    }
  }

  private updatePartialRecursive(tabulations: TabulationI[], data?: Partial<TabulationI>) {
    const { _id } = data as Partial<TabulationI>;
    const idx = tabulations.findIndex((tabulation) => tabulation._id === _id);
    if (idx !== -1) {
      tabulations[idx] = { ...tabulations[idx], ...(data ?? {}) };
      return;
    }
    for (const tabulation of tabulations) {
      this.updatePartialRecursive(tabulation.tabulationsDaughters ?? [], data);
    }
  }

  private deleteRecursive(tabulations: TabulationI[], data?: Partial<TabulationI>) {
    const { _id } = data as Partial<TabulationI>;
    const idx = tabulations.findIndex((tabulation) => tabulation._id === _id);
    if (idx !== -1) {
      tabulations.splice(idx, 1);
      return;
    }
    for (const tabulation of tabulations) {
      this.deleteRecursive(tabulation.tabulationsDaughters ?? [], data);
    }
  }

  private filterActiveTabulationsRecursive(tabulations: TabulationI[]): TabulationI[] {
    return tabulations
      .filter((tabulation) => tabulation.status === 'active')
      .map((tabulation) => ({
        ...tabulation,
        tabulationsDaughters: tabulation.tabulationsDaughters
          ? this.filterActiveTabulationsRecursive(tabulation.tabulationsDaughters)
          : undefined,
      }));
  }

  public getTabulations(
    customerId: string,
    query: TabulationFilters = {
      search: '',
      sort: 'desc',
      active: 'createdAt',
      limit: 999,
      page: 1,
      status: '',
      team: [''] || '',
      level: TabulationLevelE.FIRST.toString(),
      channel: '',
      empyChannels: false,
    }
  ): Observable<PageI<TabulationI>> {
    const queryParams = {
      level: TabulationLevelE.FIRST.toString(),
      ...query,
    };
    const { search, sort, active, limit, page, status, level, channel, empyChannels } = queryParams;
    let params = new HttpParams();
    const addParamHttpNonEmpty = (key: string, value: string | number | boolean | undefined) => {
      if (value && value !== undefined && value !== '') {
        params = params.set(key, value);
      }
    };

    const teamsQuery = this.transformTeamsQueryParam(query.team);

    addParamHttpNonEmpty('sort', sort);
    addParamHttpNonEmpty('active', active);
    addParamHttpNonEmpty('limit', limit.toString());
    addParamHttpNonEmpty('page', page.toString());
    addParamHttpNonEmpty('customerId', customerId);
    addParamHttpNonEmpty('search', search);
    addParamHttpNonEmpty('status', status);
    addParamHttpNonEmpty('team', teamsQuery);
    addParamHttpNonEmpty('level', level);
    addParamHttpNonEmpty('channel', channel);

    if (channel && empyChannels) {
      addParamHttpNonEmpty('empyChannels', true);
    }

    return this.http.get<PageI<TabulationI>>(`${this.baseUrl}${this.slug_url}`, { params });
  }

  public transformTeamsQueryParam(teams?: string[] | string) {
    if (!teams) {
      return;
    }

    return Array.isArray(teams) ? teams.join(',') : teams;
  }

  public async getTabulationsByChannelId(channelId?: string) {
    const tabulations = await firstValueFrom(this.tabulations$);
    if (!channelId) {
      return tabulations;
    }
    return tabulations.filter((tabulation) => {
      const channels = (tabulation.channels ?? []) as ChannelI[];
      const found = !!channels.find((channel) => channel._id === channelId);
      return found || channels.length === 0;
    });
  }

  public async getActiveTabulationsByChannelId(channelId?: string) {
    const tabulations = await firstValueFrom(this.tabulations$);
    const activeTabulations = this.filterActiveTabulationsRecursive(tabulations);

    if (!channelId) {
      return activeTabulations;
    }

    return activeTabulations.filter((tabulation) => {
      const channels = (tabulation.channels ?? []) as ChannelI[];
      const found = !!channels.find((channel) => channel._id === channelId);
      return found || channels.length === 0;
    });
  }

  private async loadAllCustomerTabulations(user: UserAttributesI, channelId: string = ''): Promise<TabulationI[]> {
    const team = this.userHelper.getDefaultTeamFilter();

    const tabulations: PageI<TabulationI> = await firstValueFrom(
      this.getTabulations(user['custom:customer'], {
        status: 'active',
        limit: 999,
        page: 1,
        sort: 'asc',
        active: 'name',
        team,
        channel: channelId,
        empyChannels: true,
      } as any)
    );
    return tabulations?.data ?? [];
  }

  public getTabulationById(id: string): Observable<TabulationI> {
    return this.http.get<TabulationI>(`${this.baseUrl}${this.slug_url}/${id}`);
  }

  public updateTabulation(tabulation: TabulationI): Observable<TabulationI> {
    return this.http.patch<TabulationI>(`${this.baseUrl}${this.slug_url}/${tabulation._id}`, tabulation);
  }

  public createTabulation(tabulation: TabulationI): Observable<TabulationI> {
    const body = Object.assign({}, tabulation);
    delete body['_id'];

    return this.http.post<TabulationI>(`${this.baseUrl}${this.slug_url}`, body);
  }

  public createTabulationDaughter(tabulationRoot: string, tabulation: TabulationI): Observable<TabulationI> {
    const body = Object.assign({}, tabulation);
    delete body['_id'];
    delete body['tabulationsDaughters'];

    return this.http.post<TabulationI>(`${this.baseUrl}${this.slug_url}/${tabulationRoot}/tabulations-daughters`, body);
  }

  public deleteTabulation(id: string): Observable<TabulationI> {
    return this.http.delete<TabulationI>(`${this.baseUrl}${this.slug_url}/${id}`);
  }

  public countTabulation(filters: any): Observable<number> {
    return this.http.get<number>(`${this.baseUrl}${this.slug_url}/count`, { params: filters });
  }

  public findChatByTabulation(idTabulation: string): Observable<number> {
    return this.http.get<any>(`${this.baseUrl}chat/find-chats-tabulation/${idTabulation}`);
  }

  public async initOnce() {
    try {
      this.tabulationsLoadedSubject.next(false);
      const user = this.userHelper.getUserAttributes() as UserAttributesI;
      if (!user) {
        return;
      }
      this.tabulationsSocketService.init();
      const tabulations = await this.loadAllCustomerTabulations(user);
      this.tabulationsSubject.next(tabulations);
    } finally {
      this.tabulationsLoadedSubject.next(true);
    }
  }

  public async refreshTabulations(): Promise<void> {
    try {
      const user = this.userHelper.getUserAttributes() as UserAttributesI;
      if (!user) {
        return;
      }
      const tabulations = await this.loadAllCustomerTabulations(user);
      this.tabulationsSubject.next(tabulations);
    } catch {
      // keeps the cached tree if the refresh fails (e.g. offline)
    }
  }
}
